import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, Gift, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f913202c-f556-4e67-8289-1cc1bbfe81d7/artifacts/l0uotybx_Logo%20de%20claudtexa%20con%20fondo%20transparante.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const [validating, setValidating] = useState(false);

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(null);
      setReferrerName('');
      return;
    }

    setValidating(true);
    try {
      const response = await axios.get(`${API}/referrals/validate/${code}`);
      setReferralValid(true);
      setReferrerName(response.data.referrer_name);
    } catch (error) {
      setReferralValid(false);
      setReferrerName('');
    } finally {
      setValidating(false);
    }
  };

  const handleReferralChange = (e) => {
    const code = e.target.value.toUpperCase();
    setReferralCode(code);
    
    // Debounce validation
    if (code.length >= 4) {
      const timeoutId = setTimeout(() => validateReferralCode(code), 500);
      return () => clearTimeout(timeoutId);
    } else {
      setReferralValid(null);
      setReferrerName('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(email, password, name, referralValid ? referralCode : null);
      if (referralValid) {
        toast.success('¡Cuenta creada! +5 créditos de bienvenida por usar código de referido');
      } else {
        toast.success('¡Cuenta creada exitosamente!');
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <Link to="/" className="flex justify-center mb-8">
          <img src={LOGO_URL} alt="CloudTexa" className="h-20 w-auto" />
        </Link>

        {/* Form Card */}
        <div className="glass rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-white text-center mb-2">
            {t('auth.registerTitle')}
          </h1>
          <p className="text-gray-400 text-center mb-8">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="text-[#FF6B00] hover:underline">
              {t('auth.loginHere')}
            </Link>
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                {t('auth.name')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="form-input"
                placeholder="Tu nombre"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
                placeholder="tu@email.com"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="form-input pr-10"
                  placeholder="••••••••"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Referral Code Field */}
            <div>
              <label className="block text-sm text-gray-400 mb-2 flex items-center gap-2">
                <Gift className="w-4 h-4 text-orange-400" />
                {t('auth.referralCode')}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={referralCode}
                  onChange={handleReferralChange}
                  className={`form-input pr-10 font-mono tracking-wider ${
                    referralValid === true ? 'border-green-500/50' : 
                    referralValid === false ? 'border-red-500/50' : ''
                  }`}
                  placeholder={t('auth.referralPlaceholder')}
                  maxLength={8}
                  data-testid="register-referral-input"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {validating && <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />}
                  {!validating && referralValid === true && <Check className="w-5 h-5 text-green-400" />}
                  {!validating && referralValid === false && <X className="w-5 h-5 text-red-400" />}
                </div>
              </div>
              {referralValid === true && referrerName && (
                <p className="mt-2 text-sm text-green-400 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  Referido por {referrerName} - ¡Recibirás +5 créditos extra!
                </p>
              )}
              {referralValid === false && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <X className="w-4 h-4" />
                  Código de referido inválido
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 rounded-lg flex items-center justify-center gap-2"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t('auth.registerButton')
              )}
            </button>
          </form>
        </div>

        {/* Back link */}
        <p className="text-center mt-6">
          <Link to="/" className="text-gray-500 hover:text-white transition-colors text-sm">
            ← Volver al inicio
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
